import styled from '@emotion/styled';
import { Project } from '@gameonly/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Completion from './Completion';
import CompletionDetails from './CompletionDetails';
import { useValidationContext } from './context/useValidation';
import ValidateProjectModal from './ValidateProjectModal';

const Title = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const ValidateSection = ({ project }: { project: Project }) => {
  const { t } = useTranslation(['project']);
  const { showValidation } = useValidationContext();

  const isValidated = !!project.validatedAt;
  const isCompleted = project.completionRate === 100;
  const isProrata = !!project.prorataInformation;

  if (isValidated) return <ValidationInformation />;

  const alertMessage = isProrata
    ? t('project:validation-section.info-message-prorata', {
        prorata: (project.prorataInformation?.prorata! * 100).toFixed(0),
        projectName: project.prorataInformation?.projectSourceName,
      })
    : t('project:validation-section.info-message-non-prorata');

  return (
    <>
      <Title>
        <p className="h4b">{t('project:validation-section.title')}</p>
        {!isValidated && (
          <Completion project={project} onClick={showValidation} />
        )}
      </Title>
      {project.completionRate !== 100 && (
        <Alert severity="info">{alertMessage}</Alert>
      )}
      {!isCompleted && <CompleteProject project={project} />}
      {!isValidated && isCompleted && <ValidateProject />}
    </>
  );
};

const CompleteProject = ({ project }: { project: Project }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLOrSVGElement | null>(null);
  const { t } = useTranslation(['project']);

  const handleOpenCompletionDetails = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleCloseCompletionDetails = () => {
    setAnchorEl(null);
  };
  const openCompletionDetails = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={t('project:validation-section.complete-button-tooltip')}>
        <Button onClick={handleOpenCompletionDetails} color="primary">
          {t('project:validation-section.complete-button-label')}
        </Button>
      </Tooltip>
      {openCompletionDetails && (
        <CompletionDetails
          placement="bottom-end"
          uncompleted={project.uncompleted}
          open={openCompletionDetails}
          anchorEl={anchorEl}
          onClose={handleCloseCompletionDetails}
        />
      )}
    </>
  );
};

const ValidateProject = () => {
  const [openValidate, setOpenValidate] = useState<boolean>(false);
  const { t } = useTranslation(['project']);

  const handleValidateProject = (event: React.MouseEvent<HTMLElement>) => {
    setOpenValidate(true);
  };

  return (
    <>
      <Tooltip title={t('project:validation-section.validate-button-tooltip')}>
        <Button
          onClick={handleValidateProject}
          color="primary"
          startIcon={<DoneAllIcon />}
        >
          {t('project:validation-section.validate-button-label')}
        </Button>
      </Tooltip>
      {openValidate && (
        <ValidateProjectModal
          open={openValidate}
          onClose={() => setOpenValidate(false)}
        />
      )}
    </>
  );
};

const ValidationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
`;

export const ValidationInformation = () => {
  const { t } = useTranslation(['project']);
  return (
    <Title>
      <ValidationHeader>
        <CheckCircleIcon color={'inherit'} />
        <p className="h4b">
          {t('project:validation-section.validated-project')}
        </p>
      </ValidationHeader>
      <Tooltip
        title={t('project:validation-modal.validation-text')}
        arrow
        sx={{ cursor: 'pointer' }}
      >
        <InfoOutlinedIcon />
      </Tooltip>
    </Title>
  );
};

export default ValidateSection;
